import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, SERVICES, DAYS } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Btn, Card, Field, Input, Textarea } from "../components/kit";

const Chip = ({ on, onClick, children, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`h-9 px-3 rounded-full border text-sm transition-colors ${on ? "border-[#FF6B00] text-[#FF6B00] bg-[#FF6B00]/10" : "border-[#333] text-neutral-400"}`}>
    {children}
  </button>
);

const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

export default function Apply() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "", phone: "", skills: "", tools: "", start_address: "", start_zip: "", radius_miles: 15 });
  const [cats, setCats] = useState([]);
  const [svcs, setSvcs] = useState([]);
  const [days, setDays] = useState([0, 1, 2, 3, 4]);
  const [hours, setHours] = useState({ start: "08:00", end: "18:00" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/apply", {
        ...f, radius_miles: Number(f.radius_miles), categories: cats, requested_services: svcs,
        availability: { days, ...hours },
      });
      login(data.token, data.user);
      nav("/s/offers");
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen bg-[#0A0A0A] px-4 py-8">
      <form onSubmit={submit} className="max-w-md mx-auto space-y-4 fade-in" data-testid="apply-form">
        <h1 className="font-head text-4xl font-bold uppercase">Apply to Driven</h1>
        <p className="text-sm text-neutral-400">Admin approval is required before you can see jobs.</p>
        <Card className="space-y-3">
          <Field label="Full name"><Input data-testid="apply-name" value={f.name} onChange={set("name")} required /></Field>
          <Field label="Email"><Input data-testid="apply-email" type="email" value={f.email} onChange={set("email")} required /></Field>
          <Field label="Password" hint="At least 8 characters"><Input data-testid="apply-password" type="password" value={f.password} onChange={set("password")} required minLength={8} /></Field>
          <Field label="Phone"><Input data-testid="apply-phone" type="tel" value={f.phone} onChange={set("phone")} required /></Field>
        </Card>
        <Card className="space-y-3">
          <Field label="Categories">
            <div className="flex gap-2">
              <Chip testid="apply-cat-mechanical" on={cats.includes("mechanical")} onClick={() => setCats(toggle(cats, "mechanical"))}>Mechanical</Chip>
              <Chip testid="apply-cat-detailing" on={cats.includes("detailing")} onClick={() => setCats(toggle(cats, "detailing"))}>Detailing</Chip>
            </div>
          </Field>
          <Field label="Services you can perform">
            <div className="flex flex-wrap gap-2">
              {SERVICES.filter(([, , c]) => cats.includes(c)).map(([k, n]) => (
                <Chip key={k} testid={`apply-svc-${k}`} on={svcs.includes(k)} onClick={() => setSvcs(toggle(svcs, k))}>{n}</Chip>
              ))}
              {!cats.length && <span className="text-xs text-neutral-500">Choose a category first</span>}
            </div>
          </Field>
          <Field label="Skills / certifications"><Textarea data-testid="apply-skills" value={f.skills} onChange={set("skills")} /></Field>
          <Field label="Tools & equipment"><Textarea data-testid="apply-tools" value={f.tools} onChange={set("tools")} placeholder="e.g. floor jack, tire machine, extractor" /></Field>
        </Card>
        <Card className="space-y-3">
          <Field label="Starting address (optional)"><Input data-testid="apply-address" value={f.start_address} onChange={set("start_address")} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="ZIP"><Input data-testid="apply-zip" inputMode="numeric" value={f.start_zip} onChange={set("start_zip")} required /></Field>
            <Field label="Radius (miles)"><Input data-testid="apply-radius" type="number" min="1" value={f.radius_miles} onChange={set("radius_miles")} /></Field>
          </div>
          <Field label="Available days">
            <div className="flex flex-wrap gap-2">
              {DAYS.map((d, i) => <Chip key={d} testid={`apply-day-${i}`} on={days.includes(i)} onClick={() => setDays(toggle(days, i))}>{d}</Chip>)}
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="From"><Input type="time" value={hours.start} onChange={(e) => setHours({ ...hours, start: e.target.value })} /></Field>
            <Field label="To"><Input type="time" value={hours.end} onChange={(e) => setHours({ ...hours, end: e.target.value })} /></Field>
          </div>
        </Card>
        <Btn data-testid="apply-submit-btn" size="lg" className="w-full" loading={busy}>Submit application</Btn>
        <Link to="/login" className="block text-center text-sm text-neutral-500">Back to sign in</Link>
      </form>
    </div>
  );
}
